import { TFunction } from 'i18next';
import { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';

import { LoadingOverlay } from '../../../components/Loading';
import Page, { PageButtonLink, Title } from '../../../components/Page';
import { paramsEqual, updateParams } from '../../../helpers/urlParams';

import addMonths from 'date-fns/addMonths';
import endOfHour from 'date-fns/endOfHour';
import startOfHour from 'date-fns/startOfHour';
import { isAllowed, withScopes } from '../../../auth/accessToken/Allow';
import Icon from '../../../components/Icons';
import Tab from '../../../components/Tabs/Tab';
import Tabs from '../../../components/Tabs/Tabs';
import View from '../../../components/View';
import { getCurrentLanguage } from '../../../i18n';
import { Transaction } from '../../../types/checkout';
import { getGapPageArgs, getNextPageArgs, isTransaction } from '../../transactionHelpers';
import { GetTransactionReason } from '../actions';
import { State as TransactionsState } from '../reducer';
import Filters from './Filters';
import ListMessage from './ListMessage';
import Paging from './Paging';
import TransactionTable from './TransactionTable';

interface MatchParams {
    accountId: string;
}

export interface TransactionListProps extends WithTranslation, RouteComponentProps<MatchParams> {
    getTransactions: (accountId: string, params: URLSearchParams, origin: GetTransactionReason, limit?: number) => void;
    createTransactionsCsv: (aid: string, transactions: Transaction[], t: TFunction, language: string) => void;
    createTransactionsPdf: (aid: string, transactions: Transaction[], t: TFunction, language: string) => void;
    transactions: TransactionsState;
    getFilterData: (accountIds: string[], fetchLocations: boolean) => void;
    scopes: string[];
}

interface TransactionListState {
    isSearching: boolean;
    timeoutReference: number | undefined;
}

class TransactionList extends Component<TransactionListProps, TransactionListState> {
    componentDidMount() {
        const { limit, transactions, accountId, params } = this.props.transactions;
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
        const urlAccountId = this.props.match.params.accountId;
        const hasTransactions = transactions.length > 0;
        const isSameAccount = accountId === urlAccountId;
        const hasSameParams = paramsEqual(requestQueryParams, params, ['limit', 'starting_after']);
        const reason =
            hasTransactions && isSameAccount && hasSameParams
                ? GetTransactionReason.refresh
                : GetTransactionReason.mount;
        this.props.getTransactions(urlAccountId, requestQueryParams, reason);
        const fetchLocations = isAllowed([['admin:accounts'], ['admin:locations'], ['read:locations']], this.props.scopes);
        this.props.getFilterData([urlAccountId], fetchLocations);
    }

    componentDidUpdate(prevProps: TransactionListProps, prevState: any) {
        if (this.props.location.search !== prevProps.location.search) {
            const { limit } = this.props.transactions;
            const urlAccountId = this.props.match.params.accountId;
            const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
            const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
            this.props.getTransactions(urlAccountId, requestQueryParams, GetTransactionReason.update);
        }
    }

    render() {
        const { t, i18n, getTransactions, createTransactionsCsv, createTransactionsPdf } = this.props;
        const { transactions, isLoading, hasNext, isLoadingNext, checkoutConfigurations, salesLocations } = this.props.transactions;
        const limit = this.props.transactions.limit.toString();
        const accountId = this.props.match.params.accountId;
        const firstTransaction = transactions.find(isTransaction) as Transaction | undefined;

        const currency = firstTransaction ? firstTransaction.currency : 'NOK';

        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const refresh = () => {
            const refreshParams = updateParams(locationQueryParams, { limit });
            getTransactions(accountId, refreshParams, GetTransactionReason.refresh);
        };
        const changeLimit = (limit: number) => {
            const limitParams = updateParams(locationQueryParams, { limit: limit.toString() });
            getTransactions(accountId, limitParams, GetTransactionReason.mount, limit);
        };

        const next = () => {
            const nextPageArgs = getNextPageArgs(transactions);
            const nextParams = updateParams(locationQueryParams, { ...nextPageArgs, limit });
            getTransactions(accountId, nextParams, GetTransactionReason.next);
        };

        const fillGap = (id: string) => {
            const gapPageArgs = getGapPageArgs(transactions, id);
            const gapParams = updateParams(locationQueryParams, { ...gapPageArgs, limit });
            getTransactions(accountId, gapParams, GetTransactionReason.refresh);
        };

        const createCsv = () => {
            createTransactionsCsv(accountId, transactions.filter(isTransaction) as Transaction[], t, i18n.language);
        };

        const createPdf = () => {
            createTransactionsPdf(
                accountId,
                transactions.filter(isTransaction) as Transaction[],
                t,
                getCurrentLanguage(i18n)
            );
        };

        return (
            <Page title={<View justify="space-between" flex="1">
                <Title>{t('payments.transactions.title')}</Title>
                <PageButtonLink to={`/${accountId}/payments/virtual-terminal`}>
                    <Icon icon="plus" fill="currentColor" />
                    {t('payments.menu.virtual_terminal_create')}
                </PageButtonLink>
            </View>} hasEnvFrame={true}>
                <Tabs>
                    <Tab path={`/${accountId}/payments/transactions`}>
                        {t('payments.menu.all_transactions')}
                    </Tab>
                    <Tab path={`/${accountId}/payments/transactions?status=AUTHORIZED&status=PARTIALLY_CAPTURED` +
                            `&created_at.gte=${startOfHour(addMonths(new Date(), -12)).toISOString()}` +
                            `&created_at.lte=${endOfHour(addMonths(new Date(), 1)).toISOString()}`}>
                        {t('payments.menu.open_transactions')}
                    </Tab>
                    <Tab path={`/${accountId}/payments/transactions?status=DECLINED`}>
                        {t('payments.menu.declined_transactions')}
                    </Tab>
                </Tabs>
                <Filters
                    history={this.props.history}
                    location={this.props.location}
                    refresh={refresh}
                    checkoutConfig={checkoutConfigurations[accountId]}
                    salesLocations={salesLocations[accountId]}
                    createCsv={createCsv}
                    createPdf={createPdf}
                    changeLimit={changeLimit}
                    limit={parseInt(limit)}
                    accountId={accountId}
                />
                <TransactionTable transactions={transactions} accountId={accountId} fillGap={fillGap} />
                <RelativeGroup>
                    <Paging next={next} isLoading={isLoading} hasNext={hasNext} />
                    <ListMessage
                        transactions={transactions}
                        isLoading={isLoading}
                        hasNext={hasNext}
                        isLoadingNext={isLoadingNext}
                        accountId={accountId}
                        queryParams={locationQueryParams}
                        currency={currency}
                    />
                    {isLoading && <LoadingOverlay />}
                </RelativeGroup>
            </Page>
        );
    }
}

export default withScopes(withTranslation()(TransactionList));

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
`;
